<script setup lang="ts">
import { computed, ref } from "vue";
import { PopoverArrow, PopoverContent, PopoverPortal, PopoverRoot, PopoverTrigger } from "reka-ui";

import { fromUtc } from "@/services/UtilitiesService";

import { useBrandStore } from "@/stores/brand-store";

import CaseSearchDeselectFiltersItem from "@/components/CaseSearch/CaseSearchDeselectFiltersItem.vue";

import type { DeleteFilter } from "@/pages/CaseSearch.vue";
import type { CaseSearchFilterValue, CaseSearchPersistedFilter } from "@/models/CaseSearchResult";

withDefaults(defineProps<{ lastSearch: CaseSearchPersistedFilter | undefined }>(), {  })
const emit = defineEmits({ deleteFilter: null as unknown as DeleteFilter });

const open = ref(false);

const usedFilters = computed(() => {
  const usedFilters = new Map<string, CaseSearchFilterValue>();

  for (const group of Object.values(__props.lastSearch?.rawFilters ?? {})) {
    if (group == null || typeof group === "string") continue;

    for (const [key, filter] of Object.entries(group)) {
      if (filter.value == null) continue;
      usedFilters.set(key, filter);
    }
  }

  return usedFilters;
});

const filteredColumns = computed(() => {
  const caseSearch = useBrandStore().caseSearchConfig;

  return caseSearch.filter((col) => usedFilters.value.has(col.fieldPath) && col.filterConfig != null);
});

const onDeleteFilter: DeleteFilter = (...args) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  emit("deleteFilter", ...args);
  open.value = false;
};
</script>
<template>
  <PopoverRoot v-if="filteredColumns.length > 0" :modal="true" :open="open" @update:open="(state) => (open = state)">
    <PopoverTrigger class="tw-btn-lg-gray">
      {{ open ? "Hide Filters" : "Show Filters" }} ({{ usedFilters.size }})
    </PopoverTrigger>
    <PopoverPortal>
      <PopoverContent side="bottom" align="center" :as-child="true" :align-offset="3">
        <div
          role="list"
          class="tw-b-r tw-flex tw-max-w-lg tw-flex-wrap tw-gap-x-2 tw-gap-y-1 tw-overflow-y-auto tw-rounded-lg tw-bg-white tw-p-1 tw-shadow"
        >
          <template v-for="column in filteredColumns" :key="column.fieldPath">
            <template v-for="filter in [usedFilters.get(column.fieldPath)!]" :key="filter.type">
              <template v-if="filter.value != null">
                <CaseSearchDeselectFiltersItem
                  v-if="
                    filter.type === 'autocomplete' ||
                    filter.type === 'select' ||
                    filter.type === 'number' ||
                    filter.type === 'text'
                  "
                  :column="column"
                  :values="filter.value"
                  @delete-filter="onDeleteFilter"
                />
                <CaseSearchDeselectFiltersItem
                  v-else-if="filter.type === 'boolean'"
                  :column="column"
                  :values="
                    column.filterConfig?.type === 'boolean'
                      ? (column.filterConfig.options.find((pol) => String(pol.value) === filter.value)?.label ??
                        'Unknown')
                      : 'Unknown'
                  "
                  @delete-filter="onDeleteFilter"
                />
                <CaseSearchDeselectFiltersItem
                  v-else-if="filter.type === 'date-range'"
                  :column="column"
                  :values="
                    filter.value.from && !filter.value.to // Only Start Date is set
                      ? `From ${fromUtc(filter.value.from)}`
                      : filter.value.to && !filter.value.from // Only End Date is set
                        ? `Until ${fromUtc(filter.value.to)}`
                        : filter.value.from === filter.value.to // Both are set on a fixed date
                          ? `${fromUtc(filter.value.from)}`
                          : `${fromUtc(filter.value.from)} - ${fromUtc(filter.value.to)}` // Both are set on different dates
                  "
                  @delete-filter="onDeleteFilter"
                />
              </template>
            </template>
          </template>
        </div>
        <PopoverArrow class="tw-fill-white" />
      </PopoverContent>
    </PopoverPortal>
  </PopoverRoot>
</template>
